/**
 * Frontend JavaScript for the Load More block.
 * Fetches the next page and appends its posts into the associated Query Loop.
 */

/* global DOMParser */

function findQueryLoop(container) {
	const queryLoop = container.closest('.wp-block-query');
	if (queryLoop) {
		return queryLoop;
	}

	let node = container;

	while (node) {
		let sibling = node.previousElementSibling;

		while (sibling) {
			if (sibling.classList.contains('wp-block-query')) {
				return sibling;
			}
			sibling = sibling.previousElementSibling;
		}

		node = node.parentElement;
	}

	return null;
}

function getPostId(post, index, prefix) {
	const idAttr =
		post.dataset.postId ||
		post.querySelector('[data-post-id]')?.dataset.postId;

	if (idAttr) {
		return idAttr;
	}

	const link = post.querySelector('a[href]')?.href;
	const match = link?.match(/\/(\d+)\/?(?:[?#].*)?$/);

	return match ? match[1] : `${prefix}-${index}`;
}

/**
 * Finds (or creates) a single shared visually-hidden aria-live region used
 * to announce how many posts were just loaded — screen reader users get no
 * other feedback that anything happened, since focus deliberately stays on
 * the button (moving it would be more disorienting, per WAI-ARIA "Load
 * More" pattern guidance) and appended posts aren't otherwise announced.
 */
function getAnnouncer() {
	let announcer = document.getElementById('diakon-load-more-announcer');

	if (!announcer) {
		announcer = document.createElement('div');
		announcer.id = 'diakon-load-more-announcer';
		announcer.className = 'screen-reader-text';
		announcer.setAttribute('aria-live', 'polite');
		announcer.setAttribute('aria-atomic', 'true');
		document.body.appendChild(announcer);
	}

	return announcer;
}

function announce(message) {
	// Force a re-announcement even if the text is identical to last time
	// (some screen readers only announce on a change to the node's content).
	const announcer = getAnnouncer();
	announcer.textContent = '';
	window.setTimeout(() => {
		announcer.textContent = message;
	}, 50);
}

function initLoadMore(container) {
	const button = container.querySelector('.diakon-load-more__button');

	if (!button) {
		return;
	}

	const errorEl = container.querySelector('.diakon-load-more__error');
	const totalPages = parseInt(container.dataset.totalPages, 10) || 1;
	let currentPage = parseInt(container.dataset.currentPage, 10) || 1;
	let nextPageUrl = container.dataset.nextPageUrl;
	const announceSingular = container.dataset.announceSingular;
	const announcePlural = container.dataset.announcePlural;
	const defaultText = container.dataset.defaultText || button.textContent;
	const loadingText = container.dataset.loadingText || defaultText;

	function setLoading(isLoading) {
		button.disabled = isLoading;
		button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
		button.classList.toggle('is-loading', isLoading);
		button.textContent = isLoading ? loadingText : defaultText;
	}

	function showError(message) {
		if (!errorEl) {
			return;
		}
		errorEl.textContent = message;
		errorEl.hidden = false;
	}

	async function loadMore() {
		if (button.disabled || !nextPageUrl) {
			return;
		}

		setLoading(true);

		if (errorEl) {
			errorEl.hidden = true;
		}

		try {
			const queryLoop = findQueryLoop(container);
			const postContainer = queryLoop?.querySelector(
				'.wp-block-post-template'
			);

			if (!postContainer) {
				throw new Error('Query loop post template not found.');
			}

			const currentIds = new Set(
				Array.from(postContainer.children).map((post, index) =>
					getPostId(post, index, 'current')
				)
			);

			const response = await fetch(nextPageUrl);

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`);
			}

			const doc = new DOMParser().parseFromString(
				await response.text(),
				'text/html'
			);

			const fetchedPostContainer = doc.querySelector(
				'.wp-block-post-template'
			);

			if (!fetchedPostContainer) {
				throw new Error('No posts found on the next page.');
			}

			const newPosts = Array.from(fetchedPostContainer.children).filter(
				(post, index) =>
					!currentIds.has(getPostId(post, index, 'fetched'))
			);

			newPosts.forEach((post) => postContainer.appendChild(post));

			if (announceSingular && announcePlural) {
				const template =
					newPosts.length === 1 ? announceSingular : announcePlural;
				announce(template.replace('%d', newPosts.length));
			}

			currentPage += 1;

			const fetchedLoadMore = doc.querySelector('.diakon-load-more');
			nextPageUrl =
				currentPage < totalPages
					? fetchedLoadMore?.dataset.nextPageUrl || null
					: null;

			if (!nextPageUrl) {
				container.style.display = 'none';
				return;
			}

			setLoading(false);
		} catch (error) {
			showError(error.message || 'Failed to load more posts.');
			setLoading(false);
		}
	}

	button.addEventListener('click', loadMore);
}

function initAllLoadMore(container = document) {
	container.querySelectorAll('.diakon-load-more').forEach(initLoadMore);
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => initAllLoadMore());
} else {
	initAllLoadMore();
}

// Re-initialize any Load More button injected by other modules (e.g. the
// post-filter module swapping in a freshly-fetched Query Loop).
document.addEventListener('diakon:content-updated', (event) => {
	initAllLoadMore(event.detail.container);
});
